import { Loader2, Mic, X } from "lucide-react";
import { Button } from "@/ui/button";
import { spokenMemoryApi, type SpokenMemoryApi } from "@/data/spoken-memory";
import {
  RECORD_SECONDS,
  VoiceEnrollmentPhaseView,
  useVoiceEnrollmentRecording,
} from "@/components/spoken-memory/voice-enrollment-recording";
import { useEffect } from "react";

// Settings-surface wrapper around the shared browser enrollment recording
// flow (voice-enrollment-recording.tsx). Waits for the hosted voiceprint mint
// (attribution readiness) after an accepted sample; the onboarding wizard
// uses the same hook but completes on acceptance instead.
export function VoiceTestModal({
  api = spokenMemoryApi,
  workspaceSlug,
  open,
  mode,
  onClose,
  onCompleted,
}: {
  api?: SpokenMemoryApi;
  workspaceSlug?: string | null;
  open: boolean;
  mode: "start" | "redo";
  onClose: () => void;
  onCompleted: () => Promise<void>;
}) {
  const recording = useVoiceEnrollmentRecording({
    api,
    workspaceSlug,
    waitForReady: true,
    onCompleted,
  });
  const {
    phase,
    consented,
    setConsented,
    message,
    busy,
    startRecording,
    retrySpeakerSetup,
    abortRun,
    reset,
  } = recording;

  useEffect(() => {
    if (!open) return;
    reset();
    return () => {
      abortRun();
    };
  }, [abortRun, mode, open, reset]);

  if (!open) return null;

  const close = () => {
    abortRun();
    onClose();
  };

  const canClose =
    recording.canCancel ||
    phase.kind === "ready" ||
    phase.kind === "finishing_background" ||
    phase.kind === "needs_redo" ||
    phase.kind === "setup_failed" ||
    phase.kind === "failed";
  const title = mode === "redo" ? "Redo Voice Test" : "Start Voice Test";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="voice-test-title"
        className="w-full max-w-lg rounded-lg border border-border bg-card shadow-2xl"
      >
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-foreground">
              <Mic className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h3
                id="voice-test-title"
                className="truncate text-[15px] font-semibold text-foreground"
              >
                {title}
              </h3>
              <div className="text-[12px] text-muted-foreground">
                {RECORD_SECONDS}s in your browser
              </div>
            </div>
          </div>
          {canClose ? (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={close}
              aria-label="Close voice test"
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>

        <div className="space-y-4 px-4 py-4">
          {phase.kind === "idle" ? (
            <>
              <p className="text-[13px] text-muted-foreground">
                You&apos;ll read a short passage aloud for {RECORD_SECONDS}{" "}
                seconds. Find a quiet spot and make sure you&apos;re alone —
                other voices in the recording mean a redo.
              </p>
              <label className="flex items-start gap-2 text-[12px] text-muted-foreground">
                <input
                  type="checkbox"
                  checked={consented}
                  onChange={(event) => setConsented(event.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  I consent to use this voice profile for speaker matching on my
                  team&apos;s captures.
                </span>
              </label>
            </>
          ) : (
            <VoiceEnrollmentPhaseView phase={phase} />
          )}

          {message ? (
            <div className="text-[12px] text-muted-foreground">{message}</div>
          ) : null}
        </div>

        {phase.kind === "uploading" || phase.kind === "checking" ? null : (
          <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
            {phase.kind === "ready" || phase.kind === "finishing_background" ? (
              <Button type="button" onClick={close}>
                Done
              </Button>
            ) : phase.kind === "finishing" ? (
              <Button type="button" variant="outline" onClick={close}>
                Finish in background
              </Button>
            ) : phase.kind === "setup_failed" ? (
              <>
                <Button type="button" variant="outline" onClick={close}>
                  Close
                </Button>
                <Button type="button" onClick={() => void retrySpeakerSetup()}>
                  Try setup again
                </Button>
              </>
            ) : phase.kind === "needs_redo" || phase.kind === "failed" ? (
              <>
                <Button type="button" variant="outline" onClick={close}>
                  Close
                </Button>
                <Button type="button" onClick={() => void startRecording()}>
                  {phase.kind === "needs_redo"
                    ? "Redo Voice Test"
                    : "Try Again"}
                </Button>
              </>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={close}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={busy || !consented}
                  onClick={() => void startRecording()}
                >
                  {busy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : null}
                  {phase.kind === "recording"
                    ? `Recording (${phase.remainingSec}s)`
                    : busy
                      ? "Working…"
                      : title}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
